import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Clapperboard, Loader2, Eye, EyeOff, Mail, ArrowLeft, RefreshCw, Check, X } from 'lucide-react';
import { toast } from '@/lib/toast';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { SEOHead } from '@/components/SEOHead';
import { cn } from '@/lib/utils';
import HeroWave from '@/components/ui/dynamic-wave-canvas-background';

const emailSchema = z
  .string()
  .trim()
  .min(1, 'Email is required')
  .max(255, 'Email must be less than 255 characters')
  .email('Please enter a valid email address (e.g., name@example.com)');

const passwordSchema = z.string().min(6, 'Password must be at least 6 characters');

export default function Auth() {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
  
  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginEmailError, setLoginEmailError] = useState('');
  
  // Signup form
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupUsername, setSignupUsername] = useState('');
  const [signupEmailError, setSignupEmailError] = useState('');
  
  // Username availability
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const [usernameError, setUsernameError] = useState('');
  const usernameCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Email verification pending state
  const [emailVerificationPending, setEmailVerificationPending] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [pendingUsername, setPendingUsername] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const cooldownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Forgot password
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  
  const { signIn, signUp, resetPassword, user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
      if (usernameCheckTimeoutRef.current) {
        clearTimeout(usernameCheckTimeoutRef.current);
      }
    };
  }, []);

  // Check username availability
  const checkUsernameAvailability = async (username: string) => {
    const trimmed = username.trim().toLowerCase();
    
    if (!trimmed || trimmed.length < 3) {
      setUsernameStatus('idle');
      setUsernameError('');
      return;
    }

    // Validate format locally first
    if (!/^[a-zA-Z0-9_]+$/.test(trimmed)) {
      setUsernameStatus('invalid');
      setUsernameError('Only letters, numbers, and underscores');
      return;
    }

    if (trimmed.length > 20) {
      setUsernameStatus('invalid');
      setUsernameError('Maximum 20 characters');
      return;
    }

    setUsernameStatus('checking');

    try {
      const { data, error } = await supabase.functions.invoke('check-username', {
        body: { username: trimmed }
      });

      if (error) throw error;

      if (data.error) {
        setUsernameStatus('invalid');
        setUsernameError(data.error);
      } else if (data.available) {
        setUsernameStatus('available');
        setUsernameError('');
      } else {
        setUsernameStatus('taken');
        setUsernameError('Username is already taken');
      }
    } catch (err) {
      setUsernameStatus('idle');
      console.error('Failed to check username:', err);
    }
  };

  const handleUsernameChange = (value: string) => {
    // Only allow valid characters
    const filtered = value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase();
    setSignupUsername(filtered);
    setUsernameStatus('idle');
    setUsernameError('');

    // Clear previous timeout
    if (usernameCheckTimeoutRef.current) {
      clearTimeout(usernameCheckTimeoutRef.current);
    }

    // Debounce the check (500ms)
    if (filtered.length >= 3) {
      usernameCheckTimeoutRef.current = setTimeout(() => {
        checkUsernameAvailability(filtered);
      }, 500);
    }
  };

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  const validateLoginEmail = (value: string) => {
    const result = emailSchema.safeParse(value);
    if (!result.success) {
      setLoginEmailError(result.error.errors[0].message);
      return false;
    }
    setLoginEmailError('');
    return true;
  };

  const validateSignupEmail = (value: string) => {
    const result = emailSchema.safeParse(value);
    if (!result.success) {
      setSignupEmailError(result.error.errors[0].message);
      return false;
    }
    setSignupEmailError('');
    return true;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    if (!validateLoginEmail(loginEmail)) {
      return;
    }
    
    // Validate password
    const passwordResult = passwordSchema.safeParse(loginPassword);
    if (!passwordResult.success) {
      toast.error({ title: 'Invalid Password', description: passwordResult.error.errors[0].message });
      return;
    }
    
    setIsLoading(true);
    try {
      const { error } = await signIn(loginEmail, loginPassword);
      
      if (error) {
        let message = error.message;
        const errorStatus = (error as any)?.status;
        
        // Handle specific error cases
        if (message.includes('Invalid login credentials')) {
          message = 'Invalid email or password. Please try again.';
        } else if (message.includes('Email not confirmed')) {
          message = 'Please verify your email before logging in. Check your inbox for the verification link.';
        } else if (errorStatus === 429 || message.toLowerCase().includes('rate limit')) {
          message = 'Too many login attempts. Please wait a few minutes and try again.';
        } else if (message.toLowerCase().includes('fetch') || message.toLowerCase().includes('network') || message.includes('Failed to fetch')) {
          message = 'Connection failed. Please check your internet connection and try again.';
        }
        toast.error({ title: 'Login Failed', description: message });
      } else {
        toast.success({ title: 'Welcome Back!', description: 'You have successfully logged in.' });
        navigate('/');
      }
    } catch (err: any) {
      // Handle unexpected errors (network failures, etc.)
      const message = err?.message?.toLowerCase().includes('fetch') || err?.message?.toLowerCase().includes('network')
        ? 'Connection failed. Please check your internet connection.'
        : 'An unexpected error occurred. Please try again.';
      toast.error({ title: 'Login Failed', description: message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check username
    if (!signupUsername || signupUsername.trim().length < 3) {
      toast.error({ title: 'Username Required', description: 'Please enter a username (at least 3 characters).' });
      return;
    }
    
    if (usernameStatus === 'taken' || usernameStatus === 'invalid') {
      toast.error({ title: 'Invalid Username', description: usernameError || 'Please choose a different username.' });
      return;
    }
    
    if (usernameStatus === 'checking') {
      toast.info({ title: 'Please Wait', description: 'Checking username availability...' });
      return;
    }
    
    // Validate email
    if (!validateSignupEmail(signupEmail)) {
      return;
    }
    
    // Validate password
    const passwordResult = passwordSchema.safeParse(signupPassword);
    if (!passwordResult.success) {
      toast.error({ title: 'Invalid Password', description: passwordResult.error.errors[0].message });
      return;
    }
    
    setIsLoading(true);
    try {
      const { error, data } = await signUp(signupEmail, signupPassword, signupUsername);
      
      if (error) {
        let message = error.message;
        const errorStatus = (error as any)?.status;
        
        // Handle specific error cases
        if (message.includes('User already registered')) {
          message = 'An account with this email already exists. Please log in instead.';
        } else if (message.includes('Username is already taken')) {
          message = 'This username is already taken. Please choose another.';
        } else if (errorStatus === 429 || message.toLowerCase().includes('rate limit')) {
          message = 'Too many signup attempts. Please wait a few minutes and try again.';
        } else if (message.toLowerCase().includes('fetch') || message.toLowerCase().includes('network') || message.includes('Failed to fetch')) {
          message = 'Connection failed. Please check your internet connection and try again.';
        }
        toast.error({ title: 'Signup Failed', description: message });
      } else {
        const needsVerification = data?.user && (!data.user.identities?.length || !data.user.email_confirmed_at);
        
        if (needsVerification) {
          setPendingEmail(signupEmail);
          setPendingUsername(signupUsername);
          setEmailVerificationPending(true);
          toast.info({
            title: 'Verify Your Email',
            description: `We sent a confirmation link to ${signupEmail}`,
          });
        } else {
          toast.success({ title: 'Account Created!', description: 'Welcome to CineRig.' });
          navigate('/');
        }
      }
    } catch (err: any) {
      const message = err?.message?.toLowerCase().includes('fetch') || err?.message?.toLowerCase().includes('network')
        ? 'Connection failed. Please check your internet connection.'
        : 'An unexpected error occurred. Please try again.';
      toast.error({ title: 'Signup Failed', description: message });
    } finally {
      setIsLoading(false);
    }
  };

  const sendBrandedVerificationEmail = async (email: string, displayName?: string) => {
    const { error } = await supabase.functions.invoke('send-verification-email', {
      body: {
        email,
        displayName,
        redirectTo: `${window.location.origin}/`
      }
    });
    
    if (error) {
      console.error('Error sending branded email:', error);
      throw error;
    }
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0 || isResending) return;
    
    setIsResending(true);
    
    try {
      // Only use our branded Resend email - it generates a fresh verification link
      await sendBrandedVerificationEmail(pendingEmail, pendingUsername);
      
      toast.success({ title: 'Email Sent!', description: 'Check your inbox for the verification link.' });
      setResendCooldown(60);
      
      // Start cooldown timer
      cooldownIntervalRef.current = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            if (cooldownIntervalRef.current) {
              clearInterval(cooldownIntervalRef.current);
              cooldownIntervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      let message = error.message;
      if (message?.toLowerCase().includes('fetch') || message?.toLowerCase().includes('network')) {
        message = 'Connection failed. Please check your internet connection.';
      } else if (error?.status === 429 || message?.toLowerCase().includes('rate limit')) {
        message = 'Too many requests. Please wait a few minutes.';
      }
      toast.error({ title: 'Failed to Resend', description: message });
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToSignup = () => {
    setEmailVerificationPending(false);
    setPendingEmail('');
    setPendingUsername('');
    setResendCooldown(0);
    if (cooldownIntervalRef.current) {
      clearInterval(cooldownIntervalRef.current);
      cooldownIntervalRef.current = null;
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const emailResult = emailSchema.safeParse(forgotPasswordEmail);
    if (!emailResult.success) {
      toast.error({ title: 'Invalid Email', description: emailResult.error.errors[0].message });
      return;
    }
    
    setForgotPasswordLoading(true);
    try {
      const { error } = await resetPassword(forgotPasswordEmail);
      
      if (error) {
        let message = error.message;
        const errorStatus = (error as any)?.status;
        
        if (errorStatus === 429 || message?.toLowerCase().includes('rate limit')) {
          message = 'Too many reset requests. Please wait a few minutes.';
        } else if (message?.toLowerCase().includes('fetch') || message?.toLowerCase().includes('network')) {
          message = 'Connection failed. Please check your internet connection.';
        }
        toast.error({ title: 'Error', description: message });
      } else {
        setForgotPasswordSent(true);
      }
    } catch (err: any) {
      const message = err?.message?.toLowerCase().includes('fetch') || err?.message?.toLowerCase().includes('network')
        ? 'Connection failed. Please check your internet connection.'
        : 'An unexpected error occurred. Please try again.';
      toast.error({ title: 'Error', description: message });
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const openForgotPassword = () => {
    setForgotPasswordEmail(loginEmail);
    setForgotPasswordSent(false);
    setForgotPasswordOpen(true);
  };

  if (authLoading) {
    return (
      <>
        <HeroWave />
        <div className="relative min-h-screen flex items-center justify-center">
          <div className="pointer-events-none absolute inset-0 bg-background/80" />
          <Loader2 className="relative z-10 w-8 h-8 animate-spin text-primary" />
        </div>
      </>
    );
  }

  // Email Verification Pending UI
  if (emailVerificationPending) {
    return (
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <SEOHead title="Verify Email" noIndex />
        <HeroWave />
        <div className="pointer-events-none absolute inset-0 bg-background/80" />
        <div className="relative z-10 w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg shadow-primary/20">
              <Clapperboard className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <span className="font-display font-bold text-2xl">Cine</span>
              <span className="text-primary font-display font-bold text-2xl">Rig</span>
              <span className="text-muted-foreground font-display text-2xl">.pro</span>
            </div>
          </div>

          <Card className="glass-strong border-border/50">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Mail className="w-8 h-8 text-primary animate-pulse" />
              </div>
              <CardTitle className="font-display text-xl">Check Your Email</CardTitle>
              <CardDescription className="mt-2">
                We sent a verification link to
              </CardDescription>
              <p className="font-medium text-foreground mt-1">{pendingEmail}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg bg-muted/50 border border-border p-4 text-center">
                <p className="text-sm text-muted-foreground">
                  Click the link in your email to verify your account and get started.
                </p>
              </div>
              
              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleResendVerification}
                  disabled={resendCooldown > 0 || isResending}
                >
                  {isResending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  {resendCooldown > 0 
                    ? `Resend in ${resendCooldown}s` 
                    : 'Resend Verification Email'
                  }
                </Button>
                
                <Button
                  variant="ghost"
                  className="w-full text-muted-foreground"
                  onClick={handleBackToSignup}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Use a Different Email
                </Button>
              </div>
              
              <p className="text-xs text-center text-muted-foreground">
                Didn't receive the email? Check your spam folder or try resending.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4">
      <SEOHead title="Sign In" noIndex />
      <HeroWave />
      <div className="pointer-events-none absolute inset-0 bg-background/80" />
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg shadow-primary/20">
            <Clapperboard className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <span className="font-display font-bold text-2xl">Cine</span>
            <span className="text-primary font-display font-bold text-2xl">Rig</span>
            <span className="text-muted-foreground font-display text-2xl">.pro</span>
          </div>
        </div>

        <Card className="glass-strong border-border/50">
          <CardHeader className="text-center">
            <CardTitle className="font-display text-xl">Welcome</CardTitle>
            <CardDescription>
              Sign in to your account or create a new one
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'login' | 'signup')}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login">Login</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>
              
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@example.com"
                      value={loginEmail}
                      onChange={(e) => {
                        setLoginEmail(e.target.value);
                        if (loginEmailError) setLoginEmailError('');
                      }}
                      onBlur={() => loginEmail && validateLoginEmail(loginEmail)}
                      className={loginEmailError ? 'border-destructive focus-visible:ring-destructive' : ''}
                      required
                      autoComplete="email"
                    />
                    {loginEmailError && (
                      <p className="text-sm text-destructive">{loginEmailError}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="link"
                      className="px-0 text-sm text-muted-foreground hover:text-primary"
                      onClick={openForgotPassword}
                    >
                      Forgot password?
                    </Button>
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Sign In
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="signup">
                <form onSubmit={handleSignup} className="space-y-4">
                  {/* Username - Required */}
                  <div className="space-y-2">
                    <Label htmlFor="signup-username">
                      Username <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</div>
                      <Input
                        id="signup-username"
                        type="text"
                        placeholder="username"
                        value={signupUsername}
                        onChange={(e) => handleUsernameChange(e.target.value)}
                        autoComplete="username"
                        className={cn(
                          "pl-7",
                          usernameStatus === 'taken' && 'border-destructive focus-visible:ring-destructive',
                          usernameStatus === 'invalid' && 'border-destructive focus-visible:ring-destructive',
                          usernameStatus === 'available' && 'border-green-500 focus-visible:ring-green-500'
                        )}
                        required
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {usernameStatus === 'checking' && (
                          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                        )}
                        {usernameStatus === 'available' && (
                          <Check className="w-4 h-4 text-green-500" />
                        )}
                        {(usernameStatus === 'taken' || usernameStatus === 'invalid') && (
                          <X className="w-4 h-4 text-destructive" />
                        )}
                      </div>
                    </div>
                    {usernameError ? (
                      <p className="text-sm text-destructive">{usernameError}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">3-20 characters, letters, numbers, underscores</p>
                    )}
                  </div>
                  
                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">
                      Email <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      value={signupEmail}
                      onChange={(e) => {
                        setSignupEmail(e.target.value);
                        if (signupEmailError) setSignupEmailError('');
                      }}
                      onBlur={() => signupEmail && validateSignupEmail(signupEmail)}
                      className={signupEmailError ? 'border-destructive focus-visible:ring-destructive' : ''}
                      required
                      autoComplete="email"
                    />
                    {signupEmailError && (
                      <p className="text-sm text-destructive">{signupEmailError}</p>
                    )}
                  </div>
                  
                  {/* Password */}
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">
                      Password <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="signup-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={signupPassword}
                        onChange={(e) => setSignupPassword(e.target.value)}
                        required
                        autoComplete="new-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">Minimum 6 characters</p>
                  </div>
                  
                  <Button type="submit" className="w-full" disabled={isLoading || usernameStatus === 'checking' || usernameStatus === 'taken' || usernameStatus === 'invalid'}>
                    {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Create Account
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        
        <p className="text-center text-sm text-muted-foreground mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          {forgotPasswordSent ? (
            <div className="space-y-4 py-4">
              <div className="rounded-lg bg-primary/10 border border-primary/20 p-4 text-center">
                <p className="text-sm text-foreground">
                  Check your email for a password reset link. It may take a few minutes to arrive.
                </p>
              </div>
              <Button 
                className="w-full" 
                variant="outline"
                onClick={() => setForgotPasswordOpen(false)}
              >
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="you@example.com"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setForgotPasswordOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" disabled={forgotPasswordLoading}>
                  {forgotPasswordLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Send Link
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
